import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import VirtualCard from "@/components/VirtualCard";
import ReferralCard from "@/components/ReferralCard";
import ProfileEditDialog from "@/components/ProfileEditDialog";
import TransactionsTable from "@/components/TransactionsTable";
import { SkeletonRows } from "@/components/Skeleton";
import OfferCard from "@/components/OfferCard";
import OfferCarousel from "@/components/OfferCarousel";
import PortalSections from "@/components/PortalSections";
import CustomizeViewButton from "@/components/CustomizeViewButton";
import { resolvePortalView, MEMBER_SECTIONS } from "@/lib/portalView";
import { useWhatsAppConfig } from "@/components/WhatsAppButton";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, Calendar, Globe, MessageCircle } from "lucide-react";

function InfoRow({ icon: Icon, label, value, testid }) {
  return (
    <li className="flex items-center gap-3">
      <div
        className="h-8 w-8 rounded-lg grid place-items-center flex-shrink-0"
        style={{ background: "var(--bg-soft)", color: "var(--gold)" }}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="label-caps">{label}</div>
        <div className="text-sm truncate" data-testid={testid}>
          {value || "—"}
        </div>
      </div>
    </li>
  );
}

function ProfileCard({ profile, loading, joinDate, onSaved }) {
  return (
    <div className="w-card w-card-bordered p-6 h-full" data-testid="profile-card">
      <div className="flex items-center justify-between gap-2">
        <div className="label-caps">Profile</div>
        <div className="flex items-center gap-2">
          <ProfileEditDialog profile={profile} onSaved={onSaved} />
          <a href="https://wa.me/971527184369?text=Hi%20Zola%20Rewards%20support%2C%20I%20need%20help%20with%20my%20account."
             target="_blank" rel="noreferrer" data-testid="whatsapp-support-btn"
             className="inline-flex items-center gap-2 rounded-full h-9 px-4 text-sm font-semibold"
             style={{ background: "rgba(37,211,102,0.14)", color: "#128c4a", border: "1px solid rgba(37,211,102,0.32)" }}>
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
        </div>
      </div>
      <div className="mt-2 text-xl font-bold" style={{ letterSpacing: "-0.01em" }}>
        {loading ? <span className="skeleton block h-6 w-40" /> : profile?.name}
      </div>
      <ul className="mt-5 space-y-4">
        <InfoRow icon={Mail} label="Email" value={profile?.email} testid="profile-email" />
        <InfoRow icon={Phone} label="Phone" value={profile?.phone} testid="profile-phone" />
        <InfoRow icon={Globe} label="Country" value={profile?.country_code} />
        <InfoRow icon={MessageCircle} label="Contact via" value={profile?.preferred_channel} />
        <InfoRow icon={Calendar} label="Member since" value={joinDate} />
      </ul>
      <div className="mt-6 rounded-xl p-4 text-xs" style={{ background: "var(--bg-soft)", color: "var(--text-muted)", border: "1px solid var(--border)" }}>
        To earn or redeem, visit our office or contact your Zola advisor.
      </div>
    </div>
  );
}

export default function CustomerDashboard() {
  const [profile, setProfile] = useState(null);
  const [txs, setTxs] = useState([]);
  const [offers, setOffers] = useState([]);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const wa = useWhatsAppConfig();

  useEffect(() => {
    (async () => {
      try {
        const [me, tx, of, rf] = await Promise.all([
          api.get("/auth/me"),
          api.get("/transactions"),
          api.get("/offers"),
          api.get("/customer/me/referral"),
        ]);
        setProfile(me.data.profile);
        setTxs(tx.data);
        setOffers(of.data);
        setReferral(rf.data);
      } catch (e) {
        toast.error(formatError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const joinDate = profile?.join_date
    ? new Date(profile.join_date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "—";

  const view = resolvePortalView(profile?.portal_view);
  const setMode = async (id, mode) => {
    const prev = profile?.portal_view;
    setProfile((p) => ({ ...p, portal_view: { ...(p?.portal_view || {}), [id]: mode } }));
    try {
      await api.put("/customer/me/portal-view", { [id]: mode });
    } catch (e) {
      setProfile((p) => ({ ...p, portal_view: prev }));
      toast.error(formatError(e));
    }
  };

  const membership = loading ? (
    <div className="zola-card p-8 min-h-[240px]">
      <div className="skeleton h-3 w-32" style={{ background: "rgba(255,255,255,0.1)" }} />
      <div className="skeleton h-12 w-56 mt-8" style={{ background: "rgba(255,255,255,0.15)" }} />
    </div>
  ) : <VirtualCard customer={profile} />;
  const profileCard = <ProfileCard profile={profile} loading={loading} joinDate={joinDate} onSaved={setProfile} />;
  const referralCard = referral ? <ReferralCard referral={referral} /> : null;
  const nodes = { membership, profile: profileCard, referral: referralCard };
  const memberItems = Object.keys(MEMBER_SECTIONS)
    .filter((id) => view[id] === "icon" && nodes[id])
    .map((id) => ({ id, ...MEMBER_SECTIONS[id], node: nodes[id] }));
  const showCard = view.membership === "expanded", showProfile = view.profile === "expanded";

  return (
    <AppShell
      title={loading ? " " : `Hello, ${profile?.name?.split(" ")[0] || "Traveller"}`}
      subtitle="Your rewards, at a glance."
      actions={<CustomizeViewButton view={view} onChange={setMode} disabled={loading} />}
    >
      {(showCard || showProfile) && (
        <section className="grid lg:grid-cols-3 gap-6" data-testid="customer-dashboard">
          {showCard && <div className={`${showProfile ? "lg:col-span-2" : "lg:col-span-3"} min-w-0`} data-testid="member-expanded-membership">{membership}</div>}
          {showProfile && <div className={showCard ? "" : "lg:col-span-3"} data-testid="member-expanded-profile">{profileCard}</div>}
        </section>
      )}

      {!loading && referral && view.referral === "expanded" && (
        <section className={showCard || showProfile ? "mt-6" : ""} data-testid="referral-section">
          <ReferralCard referral={referral} />
        </section>
      )}

      <PortalSections memberId={profile?.customer_id} memberName={profile?.name} memberItems={loading ? [] : memberItems} deals={(dealsTitle = "Featured Partners") =>
      <section data-testid="offers-section">
        {offers.length === 0 ? (
          <>
            <h2 className="text-xl font-bold mb-4" style={{ letterSpacing: "-0.01em" }} data-testid="portal-deals-title">{dealsTitle}</h2>
            <div className="w-card-flat p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>
              No offers available right now — check back soon.
            </div>
          </>
        ) : (
          <OfferCarousel testid="portal-offers-carousel" header={
            <div className="flex items-baseline gap-3">
              <h2 className="text-xl font-bold" style={{ letterSpacing: "-0.01em" }} data-testid="portal-deals-title">{dealsTitle}</h2>
              <span className="text-xs" style={{ color: "var(--text-muted)" }}>{offers.length} live</span>
            </div>
          }>
            {offers.map((o) => (
              <OfferCard key={o.id} offer={o} source="customer_portal" waNumber={wa?.enabled ? wa.number : null}
                         memberId={profile?.customer_id} />
            ))}
          </OfferCarousel>
        )}
      </section>} />

      <section className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ letterSpacing: "-0.01em" }}>
            Transaction History
          </h2>
          {!loading && (
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>
              {txs.length} record{txs.length === 1 ? "" : "s"}
            </div>
          )}
        </div>
        {loading ? <SkeletonRows rows={4} /> : <TransactionsTable transactions={txs} />}
      </section>
      <div className="h-36 md:hidden" aria-hidden />
    </AppShell>
  );
}
